import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import type { Event } from "@lezz/shared";
import { BottomSheet } from "@/shared/components/ui/bottom-sheet";
import { Heading } from "@/shared/components/ui/heading";
import { safeOpenURL } from "@/shared/lib/utils";
import { posthog } from "@/shared/lib/posthog";
import { buildGoogleCalendarUrl } from "@/features/events/lib/calendar";
import { openIcs } from "@/features/events/lib/open-ics";

type AddToCalendarSheetProps = {
  visible: boolean;
  onClose: () => void;
  event: Event;
};

type CalendarOption = {
  provider: "google" | "ics";
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
};

const OPTIONS: CalendarOption[] = [
  { provider: "google", label: "Google Calendar", icon: "logo-google" },
  { provider: "ics", label: "Apple / Outlook", icon: "calendar-outline" },
];

export function AddToCalendarSheet({
  visible,
  onClose,
  event,
}: AddToCalendarSheetProps) {
  const [foreground] = useCSSVariable(["--color-foreground"]) as [string];

  const handleSelect = async (provider: CalendarOption["provider"]) => {
    posthog?.capture("add_to_calendar_clicked", {
      event_id: event.eventId,
      provider,
    });
    if (provider === "google") {
      safeOpenURL(buildGoogleCalendarUrl(event));
    } else {
      await openIcs(event);
    }
    onClose();
  };

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View className="px-5 pt-2 pb-4">
        <Heading variant="section" className="mb-4">
          Adicionar ao calendário
        </Heading>
        <View className="gap-2">
          {OPTIONS.map((option) => (
            <Pressable
              key={option.provider}
              onPress={() => handleSelect(option.provider)}
              accessibilityRole="button"
              accessibilityLabel={`Adicionar ao ${option.label}`}
              className="border-border/50 flex-row items-center gap-3 rounded-lg border p-4 active:opacity-80"
            >
              <Ionicons name={option.icon} size={22} color={foreground} />
              <Text className="text-foreground text-base font-medium">
                {option.label}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </BottomSheet>
  );
}
